import { useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import {
  GLOBAL_GROUP_BY_RESTORED_FROM_SESSION,
  setGlobalGroupBy
} from "store/features/groupBy/groupBySlice";
import { RootState } from "store/rootReducer";
import {
  findCatalogColumn,
  getMaxBinDefault,
  withMaxBinDefault
} from "utils/groupBy/maxBinDefaults";

import { IGroupBy } from "models/groupBy";

import { useMaxBinDefaults } from "./useMaxBinDefaults";

// Module-level (not a ref): a component-instance ref resets on remount (e.g.
// PostPreview.tsx navigating away from /app and back), and `bin?.MaxBins`
// alone can't tell "never applied" apart from a user's intentional clear
// back to "" — either gap would let this fire a second time and stomp the
// clear. This flag is the single source of truth for "already ran this page
// load" and only resets on an actual page reload (fresh module evaluation).
let appliedThisPageLoad = false;
// The initial focus as first seen; the default only ever targets that focus.
let initialFocusKey: Maybe<string>;

// Also called from store/rootReducer.ts's resetSlices() on sign-out -- this
// flag lives outside any store, so nothing else reset it and the next user
// signed in on the same tab never got their own fresh-session default
// (EVA-306 review round 2 #4).
export function resetFreshSessionMaxBinDefaultAppliedFlag(): void {
  appliedThisPageLoad = false;
  initialFocusKey = undefined;
}

// A saved-filter restore owns the initial focus bin, even an unlimited "",
// and can land before the field list does.
export function retireFreshSessionMaxBinDefault(): void {
  appliedThisPageLoad = true;
}

// The field-list load re-dispatches the focus with a new title and units, so
// those don't count as someone else setting it.
function focusKey(groupBy: IGroupBy): string {
  return JSON.stringify({ ...groupBy, title: null, units: null, defaultUnit: null });
}

/**
 * EVA-306: applies the catalog Max Bins default to a fresh session's initial
 * global (well) focus. Never touches a session-restored focus, a MaxBins
 * that already has a value, a focus restored or changed before the field list
 * loads, or facility focus. Mounted once in views/Home.tsx.
 */
export function useFreshSessionMaxBinDefault(): void {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const maxBinDefaultsEnabled = useMaxBinDefaults();
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);

  useEffect(() => {
    if (appliedThisPageLoad) return;
    // The store, not the render-time value: React 17 can run this effect after
    // the field-list title/units re-sync, which the render-time focus predates.
    const focus = store.getState().groupBy.globalGroupBy;
    const key = focusKey(focus);
    initialFocusKey ??= key;
    if (key !== initialFocusKey) {
      appliedThisPageLoad = true;
      return;
    }
    if (maxBinDefaultsEnabled !== true) return;
    if (GLOBAL_GROUP_BY_RESTORED_FROM_SESSION) return;
    if (focus.bin?.MaxBins) return;
    // Waits for the field list: until it loads, the catalog default is unknown.
    if (!categoryList?.length) return;

    appliedThisPageLoad = true;
    const column = findCatalogColumn(categoryList, focus.property);
    if (getMaxBinDefault(column) == null) return;

    dispatch(
      setGlobalGroupBy({
        ...focus,
        bin: withMaxBinDefault(focus.bin, column)
      })
    );
  }, [dispatch, store, maxBinDefaultsEnabled, globalGroupBy, categoryList]);
}
